// #34 follow-up (task-review finding 1) — thread the already-existing MySQL
// `@@lower_case_table_names` probe into `RelationIdentityContext` at the
// rename / find-references production call sites.
//
// The schema snapshot's own probe lives in `mysqlSchemaIntrospection.ts`
// (`fetchSessionVars`) and is folded into the connection's native MySQL schema
// snapshot (`MysqlSchemaSnapshot.lowerCaseTableNames`), which
// `mysqlSchemaCache.ts` caches per connection. This module is the single seam
// that reads it and gates it to mysql connections — every production caller
// that builds a `PlanRenameInput` / `FindReferencesDeps` / an index key policy
// goes through `getMysqlLowerCaseTableNamesFor`.
//
// The navigational features also keep their OWN remembered value per
// connection (`rememberMysqlLowerCaseTableNames`, stored in
// `casePolicyState.ts`), and it is what the lifecycle actually gates on: the
// identifier index needs the answer BEFORE its first record (a Find References
// request can build the index before the schema snapshot has run —
// `probeMysqlLowerCaseTableNames` asks the server one row for it), and it must
// stay known while `ddlInvalidator.ts` drops and rebuilds the schema snapshot
// after DDL — exactly the moment the invalidator consults it. Forgotten on
// disconnect (`forgetCasePolicy`), which also invalidates any probe still in
// flight.
//
// The snapshot itself is NOT dropped on disconnect — it is `mysqlSchemaCache`'s,
// shared with completion and hover — so a snapshot that outlived a teardown
// would answer for the previous server after the id is reconnected elsewhere.
// Its `builtAt` cannot separate the two: it is stamped when the load COMPLETES
// (`mysqlSchemaCache.runMysqlPhase1`), and `publishDegraded` re-stamps the
// previous snapshot, so a load of the OLD server that was still running at the
// teardown publishes a `complete` snapshot dated after it. Two rules follow:
//
//   - The PROBE never consults the snapshot. `probeMysqlLowerCaseTableNames`
//     asks the server one row whenever this layer has not remembered an answer
//     for the CURRENT generation. That is the only value that cannot be stale,
//     and it is one row.
//   - The synchronous accessor consults the snapshot only for a connection id
//     that has NEVER been torn down (`casePolicyForgottenAt === undefined`),
//     and never adopts what it reads — a remembered snapshot value would
//     outlive the snapshot and become indistinguishable from a probed one.
//
// The same probe also brings back `@@lower_case_file_system`, because MySQL
// hands ONE component of an object identity to the file system rather than to
// `lower_case_table_names`: a TRIGGER name (see
// `mysqlTriggerNamesCaseSensitive`). It is one extra column on a row this layer
// already asks for, and it is remembered next to the mode in
// `casePolicyState.ts` — one generation guard, one teardown, one store. It is
// deliberately NOT part of the lifecycle's index-build gate, which keys on the
// MODE alone (`connection-lifecycle/navCasePolicy.ts`).
//
// Returns `undefined` for every non-mysql engine, or when nothing has learnt
// the value yet — callers then fall back to `relationIdentity.ts`'s documented
// case-insensitive default, which is the same safe, over-inclusive behavior
// the code had before this fix. The identifier index is the exception: the
// lifecycle refuses to build one until the value is known, because an index
// keyed on the wrong policy cannot be repaired afterwards.

import type { DatabaseEngineId } from '@/core/engines';

import { getCachedMysqlSchema } from '../connections/services/language-services/mysqlSchemaCache';

import {
  __resetCasePolicyStateForTest,
  casePolicyForgottenAt,
  casePolicyGeneration,
  publishCasePolicy,
  readCasePolicy,
  rememberCasePolicy,
} from './casePolicyState';
import type { ObjectKeyCase } from './index/identifierIndex';
import { asString, fetchRows, type RowMap } from './index/engines/queryHelper';
import type { ObjectKind } from './types';

export function getMysqlLowerCaseTableNamesFor(
  connectionId: string,
  engineId: DatabaseEngineId,
): number | undefined {
  if (engineId !== 'mysql') return undefined;
  const known = readCasePolicy(connectionId).mysqlLowerCaseTableNames;
  if (known !== undefined) return known;
  // The nav layer's OWN remembered value dies with the connection
  // (`forgetCasePolicy`), but the IntelliSense schema snapshot does not: it
  // belongs to `mysqlSchemaCache.ts`, is shared with completion and hover, and
  // is invalidated on its own schedule — dropping it from here would evict a
  // cache this feature does not own. Once this id has been torn down, ANY
  // snapshot filed under it may describe the server it used to point at (see
  // the header), so it stops being consulted and only a probe answers.
  if (casePolicyForgottenAt(connectionId) !== undefined) return undefined;
  // Read, never adopt: `rememberCasePolicy` here would outlive the snapshot.
  return getCachedMysqlSchema(connectionId)?.lowerCaseTableNames ?? undefined;
}

/** Record the connection's `@@lower_case_table_names` (0 / 1 / 2). */
export function rememberMysqlLowerCaseTableNames(connectionId: string, value: number): void {
  rememberCasePolicy(connectionId, { mysqlLowerCaseTableNames: value });
}

/** `SELECT @@x` answers a boolean system variable as `0` / `1` on every driver
 *  this app uses; `ON` / `OFF` is accepted too rather than guessing at one.
 *  Anything else leaves the file system unknown. */
function parseLowerCaseFileSystem(value: unknown): boolean | undefined {
  const text = asString(value).trim().toUpperCase();
  if (text === '1' || text === 'ON' || text === 'TRUE') return true;
  if (text === '0' || text === 'OFF' || text === 'FALSE') return false;
  return undefined;
}

const CASE_POLICY_SQL = 'SELECT @@lower_case_table_names AS v, @@lower_case_file_system AS fs';
const LOWER_CASE_TABLE_NAMES_SQL = 'SELECT @@lower_case_table_names AS v';

/** Ask the server for both settings in ONE row, falling back to the mode alone.
 *
 *  The fallback exists for the GATE, not for the answer: the navigational
 *  lifecycle refuses to build an index until `@@lower_case_table_names` is
 *  known (`connection-lifecycle/navCasePolicy.ts`), so a server that rejects
 *  the pair — an exotic fork, a proxy that filters system variables — must not
 *  cost the connection its index as well as its trigger rule. It never
 *  publishes a DIFFERENT answer, only a smaller one: the file system stays
 *  unknown and {@link mysqlTriggerNamesCaseSensitive} falls back to the mode. */
async function fetchCasePolicyRow(connectionId: string, database: string): Promise<RowMap | undefined> {
  try {
    return (await fetchRows(connectionId, database, CASE_POLICY_SQL, 'mysql-case-policy'))[0];
  } catch {
    // Fall through to the narrower shape.
  }
  try {
    return (
      await fetchRows(
        connectionId,
        database,
        LOWER_CASE_TABLE_NAMES_SQL,
        'mysql-lower-case-table-names',
      )
    )[0];
  } catch {
    return undefined;
  }
}

/**
 * Make sure the connection's `@@lower_case_table_names` is known: reuse what
 * THIS layer remembered for the current generation, else ask the server (one
 * row, on the introspection lane) — together with `@@lower_case_file_system`,
 * which decides the TRIGGER name component and nothing else. The cached schema
 * snapshot is deliberately not consulted — see the header. A failed probe
 * leaves the value unknown — callers then use the case-insensitive default.
 */
export async function probeMysqlLowerCaseTableNames(connectionId: string, database: string): Promise<void> {
  if (readCasePolicy(connectionId).mysqlLowerCaseTableNames !== undefined) return;
  const generation = casePolicyGeneration(connectionId);
  const row = await fetchCasePolicyRow(connectionId, database);
  if (!row) return;
  const value = Number(asString(row.v));
  if (!Number.isFinite(value)) return;
  const fileSystem = parseLowerCaseFileSystem(row.fs);
  // ONE publish, so the connection having gone away (and the id been reused)
  // while the row was in flight is checked ONCE for both values — they came
  // off the same row, and a teardown that dropped one while keeping the other
  // would leave two servers' answers side by side. See `casePolicyState.ts`.
  //
  // The file-system key is present only when the server ANSWERED it: the
  // publish merges by spreading, so an explicit `undefined` would erase a value
  // already remembered rather than leave it alone.
  publishCasePolicy(connectionId, generation, {
    mysqlLowerCaseTableNames: value,
    ...(fileSystem !== undefined ? { mysqlLowerCaseFileSystem: fileSystem } : {}),
  });
}

/** How MySQL keys an object of `kind` on this connection, PER NAME COMPONENT:
 *
 *  - `schema` — a MySQL schema IS a database, and a database is a DIRECTORY on
 *    disk, so `@@lower_case_table_names = 0` (the Linux default) makes `DbA`
 *    and `dba` two different schemas. That holds for every kind, routines
 *    included: the setting governs the directory, not the object inside it.
 *  - `name` — a view follows the same setting (it is stored as a file);
 *    a trigger follows the FILE SYSTEM instead (see
 *    {@link mysqlTriggerNamesCaseSensitive}); every other kind (stored
 *    routines, constraints) is never case-sensitive, whatever the setting.
 *
 *  `undefined` while the setting is unknown. One rule for the index's object
 *  keys and the invalidator's candidate dedupe. */
export function mysqlObjectKeyCase(connectionId: string, kind: ObjectKind): ObjectKeyCase | undefined {
  const probed = getMysqlLowerCaseTableNamesFor(connectionId, 'mysql');
  if (probed === undefined) return undefined;
  const filesAreCaseSensitive = probed === 0;
  return {
    schema: filesAreCaseSensitive,
    name:
      kind === 'trigger'
        ? mysqlTriggerNamesCaseSensitive(connectionId, probed)
        : kind === 'view'
          ? filesAreCaseSensitive
          : false,
  };
}

/** Whether this server tells the trigger `trg` from the trigger `Trg`.
 *
 *  MySQL documents trigger names as the ONE object kind `lower_case_table_names`
 *  does not reach: "Trigger names are case-sensitive if the underlying file
 *  system is case-sensitive" — i.e. they follow `@@lower_case_file_system`,
 *  which is ON exactly when the data directory sits on a case-INSENSITIVE file
 *  system (a Windows- or macOS-hosted server).
 *  https://dev.mysql.com/doc/refman/8.0/en/identifier-case-sensitivity.html
 *
 *  Keying them exact in every mode was right on a Linux server (and on the
 *  docker fixture, which is why no suite could show the other half) and wrong
 *  on a Windows-hosted one, where a `DROP TRIGGER trg` then landed in a
 *  different bucket than the listing's `Trg` and the invalidator kept stale
 *  references for a trigger that no longer exists.
 *
 *  With the file system UNKNOWN — the value came from the IntelliSense schema
 *  snapshot, or `rememberMysqlLowerCaseTableNames`, or the server rejected the
 *  pair — the MODE is the best available proxy, in one direction only: mode 0
 *  is not supported on a case-insensitive file system (MySQL refuses to run a
 *  data directory initialized under a different setting, and documents 0 as
 *  unusable on Windows / macOS), so mode 0 implies a case-SENSITIVE file
 *  system. Modes 1 / 2 say nothing either way — they are the Windows / macOS
 *  defaults AND a common Linux configuration — so they take the folded,
 *  over-inclusive answer every other unknown in this layer takes. */
function mysqlTriggerNamesCaseSensitive(connectionId: string, lowerCaseTableNames: number): boolean {
  // Remembered for the CURRENT generation only (`casePolicyState.ts`), so a
  // reconnect under the same id never reads the previous server's file system.
  const lowerCaseFs = readCasePolicy(connectionId).mysqlLowerCaseFileSystem;
  if (lowerCaseFs !== undefined) return !lowerCaseFs;
  return lowerCaseTableNames === 0;
}

/** Test-only — wipe the remembered values between unit cases. Both MySQL
 *  values live in `casePolicyState.ts`, so this IS its reset; the MySQL-named
 *  seam stays because the specs that call it are about this module. */
export function __resetMysqlCasePolicyForTest(): void {
  __resetCasePolicyStateForTest();
}
